import React, { useState, useEffect, useRef } from 'react';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Search, User, ChevronDown, ChevronUp } from 'lucide-react';
import { adminAPI } from '@/contexts/AdminAuthContext';

interface User {
  id: string;
  uid: string;
  email: string;
  displayName: string;
  role: string;
  isActive: boolean;
  isBlocked: boolean;
}

interface UserSearchInputProps {
  value: string;
  onChange: (value: string) => void;
  onUserSelect: (user: User) => void;
  placeholder?: string;
  excludeRoles?: string[];
  className?: string;
  disabled?: boolean;
}

const UserSearchInput: React.FC<UserSearchInputProps> = ({
  value,
  onChange,
  onUserSelect,
  placeholder = "Search users by email or name...",
  excludeRoles = [],
  className = "",
  disabled = false
}) => {
  const [suggestions, setSuggestions] = useState<User[]>([]);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [loading, setLoading] = useState(false);
  const [selectedIndex, setSelectedIndex] = useState(-1);
  const [allUsers, setAllUsers] = useState<User[]>([]);
  
  const inputRef = useRef<HTMLInputElement>(null);
  const suggestionsRef = useRef<HTMLDivElement>(null);

  // Load all users on component mount
  useEffect(() => {
    const loadAllUsers = async () => {
      try {
        setLoading(true);
        const response = await adminAPI.getUsers({
          search: '',
          role: '',
          status: '',
          page: 1,
          limit: 1000, // Get all users
          sortBy: 'createdAt',
          sortOrder: 'desc'
        });
        setAllUsers(response.data.users);
      } catch (error) {
        console.error('Failed to load users:', error);
      } finally {
        setLoading(false);
      }
    };

    loadAllUsers();
  }, []);

  // Filter users based on search input
  useEffect(() => {
    if (!value.trim()) {
      setSuggestions([]);
      setShowSuggestions(false);
      setSelectedIndex(-1);
      return;
    }

    const searchTerm = value.toLowerCase();
    const filtered = allUsers.filter(user => {
      // Exclude users with specified roles
      if (excludeRoles.includes(user.role)) {
        return false;
      }

      // Search by email or display name
      const emailMatch = user.email.toLowerCase().includes(searchTerm);
      const nameMatch = user.displayName?.toLowerCase().includes(searchTerm);
      
      return emailMatch || nameMatch;
    });

    setSuggestions(filtered.slice(0, 10)); // Limit to 10 suggestions
    setShowSuggestions(true);
    setSelectedIndex(-1);
  }, [value, allUsers, excludeRoles]);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    onChange(e.target.value);
  };

  const handleUserClick = (user: User) => {
    onChange(user.email);
    onUserSelect(user);
    setShowSuggestions(false);
    setSelectedIndex(-1);
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (!showSuggestions || suggestions.length === 0) return;

    switch (e.key) {
      case 'ArrowDown':
        e.preventDefault();
        setSelectedIndex(prev => 
          prev < suggestions.length - 1 ? prev + 1 : 0
        );
        break;
      case 'ArrowUp':
        e.preventDefault();
        setSelectedIndex(prev => 
          prev > 0 ? prev - 1 : suggestions.length - 1
        );
        break;
      case 'Enter':
        e.preventDefault();
        if (selectedIndex >= 0 && selectedIndex < suggestions.length) {
          handleUserClick(suggestions[selectedIndex]);
        }
        break;
      case 'Escape':
        setShowSuggestions(false);
        setSelectedIndex(-1);
        break;
    }
  };

  const handleInputFocus = () => {
    if (value.trim() && suggestions.length > 0) {
      setShowSuggestions(true);
    }
  };

  const handleInputBlur = () => {
    // Delay hiding suggestions to allow clicking on them
    setTimeout(() => {
      setShowSuggestions(false);
      setSelectedIndex(-1);
    }, 200);
  };

  const getRoleBadgeColor = (role: string) => {
    switch (role) {
      case 'root_admin':
        return 'bg-red-100 text-red-800 border-red-200';
      case 'admin':
        return 'bg-purple-100 text-purple-800 border-purple-200';
      case 'user':
        return 'bg-blue-100 text-blue-800 border-blue-200';
      default:
        return 'bg-gray-100 text-gray-800 border-gray-200';
    }
  };

  const getStatusMessage = (user: User) => {
    if (excludeRoles.includes(user.role)) {
      return `Already ${user.role.replace('_', ' ')}`;
    }
    if (user.isBlocked) {
      return 'User is blocked';
    }
    if (!user.isActive) {
      return 'User is inactive';
    }
    return null;
  };

  return (
    <div className={`relative ${className}`}>
      <div className="relative">
        <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground" />
        <Input
          ref={inputRef}
          value={value}
          onChange={handleInputChange}
          onKeyDown={handleKeyDown}
          onFocus={handleInputFocus}
          onBlur={handleInputBlur}
          placeholder={placeholder}
          className="pl-10 pr-10"
          disabled={disabled}
        />
        {loading && (
          <div className="absolute right-3 top-1/2 transform -translate-y-1/2">
            <div className="animate-spin h-4 w-4 border-2 border-primary border-t-transparent rounded-full"></div>
          </div>
        )}
        {showSuggestions && (
          <ChevronUp className="absolute right-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground" />
        )}
        {!showSuggestions && value.trim() && (
          <ChevronDown className="absolute right-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground" />
        )}
      </div>

      {/* Suggestions Dropdown */}
      {showSuggestions && (
        <div
          ref={suggestionsRef}
          className="absolute z-50 w-full mt-1 bg-background border border-border rounded-md shadow-lg max-h-60 overflow-y-auto"
        >
          {suggestions.length === 0 ? (
            <div className="p-3 text-sm text-muted-foreground text-center">
              {loading ? 'Searching...' : 'No users found'}
            </div>
          ) : (
            suggestions.map((user, index) => {
              const statusMessage = getStatusMessage(user);
              const isDisabled = !!statusMessage;
              
              return (
                <div
                  key={user.id}
                  onClick={() => !isDisabled && handleUserClick(user)}
                  className={`p-3 cursor-pointer border-b border-border last:border-b-0 ${
                    selectedIndex === index
                      ? 'bg-accent/50'
                      : 'hover:bg-accent/30'
                  } ${isDisabled ? 'opacity-50 cursor-not-allowed' : ''}`}
                >
                  <div className="flex items-center justify-between">
                    <div className="flex items-center space-x-3">
                      <User className="h-4 w-4 text-muted-foreground" />
                      <div>
                        <div className="font-medium text-sm">{user.displayName || 'No name'}</div>
                        <div className="text-xs text-muted-foreground">{user.email}</div>
                      </div>
                    </div>
                    <div className="flex items-center space-x-2">
                      <Badge className={`text-xs ${getRoleBadgeColor(user.role)}`}>
                        {user.role.replace('_', ' ')}
                      </Badge>
                      {statusMessage && (
                        <span className="text-xs text-orange-600">{statusMessage}</span>
                      )}
                    </div>
                  </div>
                </div>
              );
            })
          )}
        </div>
      )}
    </div>
  );
};

export default UserSearchInput;
